// src/Lexbuild/LexBuild.jsx

import React, { useState, useEffect } from 'react';
import './lexbuild.css';

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFunctions, httpsCallable }      from 'firebase/functions';
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from 'firebase/firestore';

import AgentForm        from './AgentForm';
import AgentFlowEditor  from './AgentFlowEditor';
import AgentDeployModal from './AgentDeployModal';
import StickyChatBox    from './StickyChatBox';
import AgentEditorModal from './AgentEditorModal';
import { FaEllipsisV }  from 'react-icons/fa';

// ——— Firebase config & init —————————————————————————————————————
const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = !getApps().length
  ? initializeApp(firebaseConfig)
  : getApp();
  
const functions = getFunctions(app, 'us-central1');
const runAgentFn = httpsCallable(functions, 'runAgent');

// ——— useAgents hook ———————————————————————————————————————
function useAgents() {
  const [agents, setAgents]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db      = getFirestore();
    const collRef = collection(db, 'agents');
    const unsubscribe = onSnapshot(
      collRef,
      snap => {
        setAgents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      err => {
        console.error('Failed to load agents:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const addAgent = async agent => {
    const db = getFirestore();
    await addDoc(collection(db, 'agents'), agent);
  };

  const updateAgent = async (id, data) => {
    const db = getFirestore();
    await setDoc(doc(db, 'agents', id), data, { merge: true });
  };

  const deleteAgent = async id => {
    const db = getFirestore();
    await deleteDoc(doc(db, 'agents', id));
  };

  return { agents, loading, addAgent, updateAgent, deleteAgent };
}

// ——— AgentCard component —————————————————————————————————————
function AgentCard({
  agent,
  onRun,
  onTest,
  onEdit,
  onDeploy,
  onDelete,
  onPublish,
  onShare,
  onEmbed,
  onOpenWidget,
  onSchedule,
  onReport,
  onFork,
}) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [result,  setResult]  = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  const toggleMenu = e => {
    e.stopPropagation();
    setShowMenu(v => !v);
  };

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await onRun(agent.id);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (fn, arg) => {
    fn(arg);
    setShowMenu(false);
  };

  return (
    <div className="lb-card" onMouseLeave={() => setShowMenu(false)}>
      <div className="lb-card-head">
        <h2>{agent.name}</h2>
        {agent.public && <span className="lb-badge">Public</span>}
      </div>
      <p className="lb-type">{agent.type}</p>
      <p className="lb-desc">{agent.description}</p>

      <div className="lb-actions">
        <button
          className="lb-action-btn"
          onClick={handleRun}
          disabled={loading}
        >
          {loading ? 'Running…' : '▶️ Run'}
        </button>
        <button className="lb-action-btn" onClick={() => onTest(agent)}>
          🧪 Test
        </button>
        <button className="lb-action-btn" onClick={() => onEdit(agent)}>
          ✏️ Edit
        </button>
        <div className="more-wrapper">
          <button className="lb-action-btn" onClick={toggleMenu}>
            <FaEllipsisV />
          </button>
          {showMenu && (
            <div className="more-menu">
              <button onClick={() => handleAction(onDeploy, agent)}>🚀 Deploy</button>
              <button onClick={() => handleAction(onPublish, agent)}>📤 Publish</button>
              <button onClick={() => handleAction(onShare, agent)}>🔗 Share</button>
              <button onClick={() => handleAction(onEmbed, agent)}>⬇️ Embed</button>
              <button onClick={() => handleAction(onOpenWidget, agent)}>🧩 Widget</button>
              <button onClick={() => handleAction(onSchedule, agent)}>📅 Schedule</button>
              <button onClick={() => handleAction(onReport, agent)}>📊 Reports</button>
              <button onClick={() => handleAction(onFork, agent)}>♻️ Fork</button>
              <hr />
              <button
                className="lb-action-btn lb-delete danger"
                onClick={() => handleAction(onDelete, agent.id)}
              >
                🗑 Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="lb-error" style={{ color: 'red', marginTop: '0.5em' }}>
          Error: {error}
        </div>
      )}
      {result && (
        <pre
          className="lb-result"
          style={{
            background: '#f5f5f5',
            padding: '0.5em',
            marginTop: '0.5em',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ——— LexBuild main component ——————————————————————————————————
export function LexBuild() {
  const { agents, loading, addAgent, updateAgent, deleteAgent } = useAgents();

  const [showForm,     setShowForm]     = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [useFlow,      setUseFlow]      = useState(false);
  const [flowData,     setFlowData]     = useState([]);
  const [formData,     setFormData]     = useState(null);
  const [deployAgent,  setDeployAgent]  = useState(null);

  const [runningId, setRunningId] = useState(null);
  const [runOutput, setRunOutput] = useState(null);
  const [runError,  setRunError]  = useState(null);

  const openForm = agent => {
    setEditingAgent(agent || null);
    setShowForm(true);
    setUseFlow(false);
    setFlowData(agent?.workflow || []);
    setFormData(null);
  };

  const handleSaveMetadata = meta => {
    setFormData(meta);
    setUseFlow(true);
  };

  const handleSaveWorkflow = ({ nodes = [], edges = [], workflow = [] }) => {
    const base    = editingAgent || {};
    const payload = { ...base, ...(formData || {}), nodes, edges, workflow };
    if (base.id) updateAgent(base.id, payload);
    else         addAgent(payload);
    setShowForm(false);
    setEditingAgent(null);
  };

  const handleRun = async agentId => {
    setRunningId(agentId);
    setRunOutput(null);
    setRunError(null);
    try {
      const { data } = await runAgentFn({ agentId });
      setRunOutput(data);
    } catch (err) {
      setRunError(err.message || String(err));
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="lexbuild-container">
      <header className="lb-header">
        <h1>🧩 Lex Build – AI Agent Builder</h1>
        <button className="lb-primary-btn" onClick={() => openForm()}>
          + Create Agent
        </button>
      </header>

      <h2 className="lb-section-title">Your Agents</h2>
      {loading ? (
        <p>Loading agents…</p>
      ) : (
        <div className="lb-grid">
          {agents.map(agent => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onRun={handleRun}
              onTest={()      => console.log('Test', agent)}
              onEdit={()      => openForm(agent)}
              onDeploy={()    => setDeployAgent(agent)}
              onDelete={()    => deleteAgent(agent.id)}
              onPublish={()   => console.log('Publish', agent)}
              onShare={()     => console.log('Share', agent)}
              onEmbed={()     => console.log('Embed', agent)}
              onOpenWidget={()=> console.log('Widget', agent)}
              onSchedule={()  => console.log('Schedule', agent)}
              onReport={()    => console.log('Report', agent)}
              onFork={()      => openForm({ ...agent, id: null })}
            />
          ))}
        </div>
      )}

      {showForm && (
        <div className="lb-modal-overlay" onClick={() => setShowForm(false)}>
          <div
            className="lb-modal"
            onClick={e => e.stopPropagation()}
            style={{ width: '80vw', height: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px', marginBottom: 8 }}>
              <h2 style={{ margin: 0 }}>{editingAgent ? 'Edit Agent' : 'New Agent'}</h2>
              <button className="lb-secondary-btn" onClick={() => setUseFlow(f => !f)}>
                {useFlow ? 'Switch to Form' : 'Switch to Flow Editor'}
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
              {useFlow ? (
                <AgentFlowEditor
                  initialNodes={editingAgent?.nodes || []}
                  initialEdges={editingAgent?.edges || []}
                  initialWorkflow={flowData || []}
                  onSave={handleSaveWorkflow}
                  onCancel={() => setShowForm(false)}
                />
              ) : (
                <AgentForm initialData={editingAgent} onSave={handleSaveMetadata} onCancel={() => setShowForm(false)} />
              )}
            </div>
          </div>
        </div>
      )}

      {deployAgent && <AgentDeployModal agent={deployAgent} onClose={() => setDeployAgent(null)} />}

      {runningId && (
        <div className="lb-modal-overlay">
          <div className="lb-modal"><p>Running… please wait</p></div>
        </div>
      )}

      {runOutput && (
        <div className="lb-modal-overlay" onClick={() => setRunOutput(null)}>
          <div className="lb-modal">
            <pre className="lb-codeblock">{JSON.stringify(runOutput, null, 2)}</pre>
            <button className="lb-primary-btn" onClick={() => setRunOutput(null)}>Close</button>
          </div>
        </div>
      )}

      {runError && (
        <div className="lb-modal-overlay" onClick={() => setRunError(null)}>
          <div className="lb-modal">
            <p style={{ color: 'red' }}>{runError}</p>
            <button className="lb-secondary-btn" onClick={() => setRunError(null)}>Close</button>
          </div>
        </div>
      )}

      <StickyChatBox onCreateFromConfig={cfg => openForm(cfg)} />
    </div>
  );
}
// continuing in the same file...




// ——— LexBuildDashboard component ——————————————————————————————————
export function LexBuildDashboard() {
  const { agents, loading, addAgent, updateAgent, deleteAgent } = useAgents();
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);

  const openCreate = () => {
    setEditingAgent(null);
    setModalOpen(true);
  };
  const openEdit = agent => {
    setEditingAgent(agent);
    setModalOpen(true);
  };

  if (loading) {
    return <div>Loading agents…</div>;
  }

  return (
    <div className="lexbuild-dashboard">
      <header className="dashboard-header">
        <h1>LexBuild: AI Agents</h1>
        <button className="lb-primary-btn" onClick={openCreate}>
          ➕ Create New Agent
        </button>
      </header>

      <div className="agent-grid">
        {agents.map(agent => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onEdit   ={() => openEdit(agent)}
            onDelete ={() => deleteAgent(agent.id)}
            onTest   ={() => {/* TODO: show test UI */}}
            onDeploy ={() => {/* TODO: open deploy modal */}}
          />
        ))}
      </div>

      <AgentEditorModal
        isOpen         ={modalOpen}
        onRequestClose ={() => setModalOpen(false)}
        existingAgent  ={editingAgent}
        onSaveNewAgent ={newAgent => {
          addAgent(newAgent);
          setModalOpen(false);
        }}
        onUpdateAgent  ={(id, updatedAgent) => {
          updateAgent(id, updatedAgent);
          setModalOpen(false);
        }}
      />
    </div>
  );
}

// … all the code for LexBuild and LexBuildDashboard above …


const _app = !getApps().length
  ? initializeApp(firebaseConfig)
  : getApp()
const _functions = getFunctions(_app, 'us-central1')
const _runAgentFn = httpsCallable(_functions, 'runAgent')

/**
 * Calls your Cloud Function using either agentId or inline agentConfig
 */
export async function runAgent(agentOrConfig, inputs = {}) {
  const payload = agentOrConfig.id
    ? { agentId: agentOrConfig.id, inputs }
    : { agentConfig: agentOrConfig, inputs }
  const { data } = await _runAgentFn(payload)
  return data
}